import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import VM from 'scratch-vm';
import {defineMessages, injectIntl, intlShape} from 'react-intl';
import log from '../lib/log';

import extensionLibraryContent, {
    galleryError,
    galleryLoading,
    galleryMore
} from '../lib/libraries/extensions/index.jsx';
import extensionTags from '../lib/libraries/tw-extension-tags';

import LibraryComponent from '../components/library/library.jsx';
import libraryStyles from '../components/library/library.css';
import extensionIcon from '../components/action-menu/icon--sprite.svg';
import ExtensionManagerModal from '../components/tw-extension-manager-modal/extension-manager-modal.jsx';
import ExtensionBlocksModal from '../components/tw-extension-blocks-modal/extension-blocks-modal.jsx';
import collaborationManager from '../lib/collaboration/collaboration-manager.js';
import {isTrustedExtension, manuallyTrustExtension} from './tw-security-manager.jsx';
import {
    getMetadata,
    subscribe,
    registerRawFetchers,
    refreshAll
} from '../lib/tw-extension-library-cache.js';

const messages = defineMessages({
    extensionTitle: {
        defaultMessage: 'Choose an Extension',
        description: 'Heading for the extension library',
        id: 'gui.extensionLibrary.chooseAnExtension'
    }
});

const toLibraryItem = extension => {
    if (typeof extension === 'object') {
        return ({
            // Default extension cards to the "featured" (large) layout so the
            // "Browse blocks" button shows up. Built-in extensions from
            // extensionLibraryContent don't set `featured` themselves.
            featured: extension.featured !== undefined ? extension.featured : true,
            rawURL: extension.iconURL || extensionIcon,
            ...extension
        });
    }
    return extension;
};

const translateGalleryItem = (extension, locale) => ({
    ...extension,
    name: extension.nameTranslations[locale] || extension.name,
    description: extension.descriptionTranslations[locale] || extension.description
});

// Frost 扩展库基地址（同源 Cloudflare，国内访问快）
const FROST_BASE = (process.env.EXTENSIONS_URL || 'https://extensions.froste.top/').replace(/\/$/, '');

// ---- 原始元数据拉取（纯 JSON，可序列化进 localStorage 缓存） ----
const TW_META_URL = 'https://extensions.turbowarp.org/generated-metadata/extensions-v0.json';
const ASTRA_META_URL = 'https://editors.astras.top/extensions/generated-metadata/extensions-v0.json';
const UDBBS_META_URL = 'https://extensions.udbbs.top/json/exts.json';
const FROST_META_URL = `${FROST_BASE}/generated-metadata/extensions-v0.json`;

const fetchTwRaw = async () => {
    const res = await fetch(TW_META_URL);
    if (!res.ok) throw new Error(`HTTP status ${res.status}`);
    const data = await res.json();
    return data.extensions;
};

const fetchAstraRaw = async () => {
    const res = await fetch(ASTRA_META_URL);
    if (!res.ok) throw new Error(`HTTP status ${res.status}`);
    const data = await res.json();
    return data.extensions;
};

const fetchUdbbsRaw = async () => {
    const res = await fetch(UDBBS_META_URL);
    if (!res.ok) throw new Error(`HTTP status ${res.status}`);
    return res.json();
};

const fetchFrostRaw = async () => {
    const res = await fetch(FROST_META_URL);
    if (!res.ok) throw new Error(`HTTP status ${res.status}`);
    const data = await res.json();
    return data.extensions || [];
};

// ---- 原始元数据 → 扩展库卡片（含 React 元素，不进缓存） ----
const transformTw = raw => (raw || []).map(extension => ({
    name: extension.name,
    nameTranslations: extension.nameTranslations || {},
    description: extension.description,
    descriptionTranslations: extension.descriptionTranslations || {},
    extensionId: extension.id,
    extensionURL: `https://extensions.turbowarp.org/${extension.slug}.js`,
    iconURL: `https://extensions.turbowarp.org/${extension.image || 'images/unknown.svg'}`,
    tags: ['tw'],
    credits: [
        ...(extension.original || []),
        ...(extension.by || [])
    ].map(credit => (
        credit.link ? (
            <a
                href={credit.link}
                target="_blank"
                rel="noreferrer"
                key={credit.name}
            >
                {credit.name}
            </a>
        ) : credit.name
    )),
    docsURI: extension.docs ? `https://extensions.turbowarp.org/${extension.slug}` : null,
    samples: extension.samples ? extension.samples.map(sample => ({
        href: `${process.env.ROOT}editor?project_url=https://extensions.turbowarp.org/samples/${encodeURIComponent(sample)}.sb3`,
        text: sample
    })) : null,
    incompatibleWithScratch: !extension.scratchCompatible,
    featured: true
}));

const transformAstra = raw => (raw || []).map((extension, index) => {
    const extensionId = extension.id || `astra-${index}-${extension.slug}`;
    return {
        name: extension.name,
        nameTranslations: extension.nameTranslations || {},
        description: extension.description,
        descriptionTranslations: extension.descriptionTranslations || {},
        extensionId,
        extensionURL: `https://editors.astras.top/extensions/${extension.slug}.js`,
        iconURL: `https://editors.astras.top/extensions/${extension.image || 'images/unknown.svg'}`,
        tags: ['astra-editor'],
        credits: (extension.by || []).map(credit => (
            credit.link ? (
                <a
                    href={credit.link}
                    target="_blank"
                    rel="noreferrer"
                    key={credit.name}
                >
                    {credit.name}
                </a>
            ) : credit.name
        )),
        docsURI: extension.docs ? `https://editors.astras.top/extensions/${extension.slug}` : null,
        incompatibleWithScratch: true,
        featured: true,
        disabled: false
    };
});

const transformUdbbs = raw => (raw || []).map((extension, index) => {
    const extensionId = `udbbs-${index}-${extension.name}`;
    // 从 description 中提取作者信息
    let credits = [];
    const creditMatch = extension.description.match(/由<a[^>]*>([^<]+)<\/a>创建/);
    if (creditMatch) {
        credits.push(creditMatch[1]);
    }
    return {
        name: extension.name,
        nameTranslations: {},
        description: extension.description.replace(/<[^>]*>/g, ''), // 移除 HTML 标签
        descriptionTranslations: {},
        extensionId,
        extensionURL: extension.url,
        iconURL: `https://extensions.udbbs.top/${extension.image || 'images/unknown.svg'}`,
        tags: ['udbbs'],
        credits,
        docsURI: null,
        incompatibleWithScratch: true,
        featured: true,
        disabled: false
    };
});

const transformFrost = raw => (raw || []).map(extension => ({
    name: extension.name,
    nameTranslations: extension.nameTranslations || {},
    description: extension.description,
    descriptionTranslations: extension.descriptionTranslations || {},
    extensionId: extension.id,
    extensionURL: `${FROST_BASE}/${extension.slug}.js`,
    iconURL: `${FROST_BASE}/${extension.image || 'images/unknown.svg'}`,
    tags: ['frosteditor'],
    credits: [
        ...(extension.original || []),
        ...(extension.by || [])
    ].map(credit => (
        credit.link ? (
            <a
                href={credit.link}
                target="_blank"
                rel="noreferrer"
                key={credit.name}
            >
                {credit.name}
            </a>
        ) : credit.name
    )),
    docsURI: extension.docs ? `${FROST_BASE}/${extension.slug}` : null,
    samples: extension.samples ? extension.samples.map(sample => ({
        href: `${process.env.ROOT}editor?project_url=${FROST_BASE}/samples/${encodeURIComponent(sample)}.sb3`,
        text: sample
    })) : null,
    incompatibleWithScratch: !extension.scratchCompatible,
    featured: true
}));

// 注册原始拉取函数，供缓存模块在启动预加载 / 后台刷新时调用
registerRawFetchers({
    tw: fetchTwRaw,
    astra: fetchAstraRaw,
    udbbs: fetchUdbbsRaw,
    frost: fetchFrostRaw
});

// 启动即预加载：应用加载阶段就在后台拉取并写入缓存，
// 用户打开扩展库面板时直接从缓存秒开，不依赖远端网络。
refreshAll();

class ExtensionLibrary extends React.PureComponent {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleItemSelect'
            ,'handleOpenAddonSettings'
            ,'handleExtensionManagerClick'
            ,'handleExtensionManagerClose'
            ,'handleRemoveExtension'
            ,'handleBrowseBlocks'
            ,'handleBrowseBlocksClose'
            ,'getLoadedExtensions'
        ]);
        const twCache = getMetadata('tw');
        const astraCache = getMetadata('astra');
        const udbbsCache = getMetadata('udbbs');
        const frostCache = getMetadata('frost');
        this.state = {
            gallery: twCache ? transformTw(twCache) : null,
            galleryError: null,
            galleryTimedOut: !twCache,
            astraExtensions: astraCache ? transformAstra(astraCache) : null,
            astraError: null,
            udbbsExtensions: udbbsCache ? transformUdbbs(udbbsCache) : null,
            udbbsError: null,
            frostExtensions: frostCache ? transformFrost(frostCache) : null,
            frostError: null,
            extensionManagerOpen: false,
            loadedExtensions: [],
            extensionInfoMap: {}, // 存储扩展 ID 到扩展信息的映射
            browseExtensionId: null, // 浏览积木弹窗当前预览的扩展 ID
            browseExtensionName: '',
            browseBlocks: null // 浏览积木弹窗直接使用传入的积木数组（不依赖 _blockInfo 反查）
        };
    }
    handleOpenAddonSettings () {
        try {
            window.dispatchEvent(new CustomEvent('open-addon-settings'));
        } catch (e) {
            // fallback: log
            if (window.console && window.console.error) console.error(e);
        }
    }
    componentDidMount () {
        // 订阅缓存更新：缓存被后台刷新写入后立即同步到面板（秒级刷新，无需重新打开）。
        const apply = () => {
            const tw = getMetadata('tw');
            const astra = getMetadata('astra');
            const udbbs = getMetadata('udbbs');
            const frost = getMetadata('frost');
            this.setState({
                gallery: tw ? transformTw(tw) : this.state.gallery,
                galleryTimedOut: tw ? false : this.state.galleryTimedOut,
                astraExtensions: astra ? transformAstra(astra) : this.state.astraExtensions,
                udbbsExtensions: udbbs ? transformUdbbs(udbbs) : this.state.udbbsExtensions,
                frostExtensions: frost ? transformFrost(frost) : this.state.frostExtensions
            });
        };
        this._unsub = subscribe(apply);
        apply();

        // 后台刷新（缓存未过期则跳过网络）；刷新结束后对仍无数据的源标记错误以便展示。
        refreshAll().then(() => {
            const sets = {galleryTimedOut: false};
            if (!getMetadata('tw')) sets.galleryError = new Error('无法加载 TurboWarp 扩展库');
            if (!getMetadata('astra')) sets.astraError = new Error('无法加载 AstraEditor 扩展库');
            if (!getMetadata('udbbs')) sets.udbbsError = new Error('无法加载 UDBBS 扩展库');
            if (!getMetadata('frost')) sets.frostError = new Error('无法加载 FrostEditor 扩展库');
            this.setState(sets);
        }).catch(() => {});

        // 监听扩展添加事件，保存扩展信息
        this.props.vm.on('EXTENSION_ADDED', this.handleExtensionAdded);

        // 初始化已加载扩展的信息（对于组件挂载前就加载的扩展）
        this.initLoadedExtensionsInfo();
    }
    initLoadedExtensionsInfo () {
        // 从 runtime 的 _blockInfo 中获取已加载扩展的信息
        const runtime = this.props.vm.runtime;
        if (runtime && runtime._blockInfo) {
            const extensionInfoMap = {};
            const coreExtensions = [
                'motion', 'looks', 'sound', 'events', 'control', 'sensing',
                'operators', 'variables', 'myBlocks'
            ];

            for (const blockInfo of runtime._blockInfo) {
                // 跳过核心扩展
                if (coreExtensions.includes(blockInfo.id)) {
                    continue;
                }

                extensionInfoMap[blockInfo.id] = {
                    id: blockInfo.id,
                    name: blockInfo.name,
                    iconURL: blockInfo.blockIconURI || blockInfo.menuIconURI || null
                };
            }

            this.setState({extensionInfoMap});
        }
    }
    handleExtensionAdded (extensionInfo) {
        // 保存扩展信息到映射中
        this.setState(prevState => ({
            extensionInfoMap: {
                ...prevState.extensionInfoMap,
                [extensionInfo.id]: {
                    id: extensionInfo.id,
                    name: extensionInfo.name,
                    iconURL: extensionInfo.blockIconURI || extensionInfo.menuIconURI || null
                }
            }
        }));
    }
    componentWillUnmount () {
        // 移除订阅与事件监听
        if (this._unsub) this._unsub();
        if (this.props.vm) {
            this.props.vm.removeListener('EXTENSION_ADDED', this.handleExtensionAdded);
        }
    }
    handleItemSelect (item) {
        if (item.href) {
            return;
        }

        const extensionId = item.extensionId;

        if (extensionId === 'custom_extension') {
            this.props.onOpenCustomExtensionModal();
            return;
        }

        if (extensionId === 'procedures_enable_return') {
            this.props.onEnableProcedureReturns();
            this.props.onCategorySelected('myBlocks');
            return;
        }

        const url = item.extensionURL ? item.extensionURL : extensionId;
        if (!item.disabled) {
            if (this.props.vm.extensionManager.isExtensionLoaded(extensionId)) {
                this.props.onCategorySelected(extensionId);
            } else {
                this.props.vm.extensionManager.loadExtensionURL(url)
                    .then(() => {
                        this.props.onCategorySelected(extensionId);
                    })
                    .catch(err => {
                        log.error(err);
                        // 直连加载失败（网络原因 script.onerror）时，经同源 /proxy 兜底重试一次。
                        // dev 走 devServer 代理、生产走 Cloudflare Worker、桌面端走协议处理器。
                        if (/^https?:\/\//i.test(url)) {
                            const proxyUrl = `/proxy?url=${encodeURIComponent(url)}`;
                            // 仅原 URL 本就是无沙箱信任源时，代理 URL 才继承信任，
                            // 防止第三方扩展借代理提权逃出沙箱。
                            if (isTrustedExtension(url)) {
                                manuallyTrustExtension(proxyUrl);
                            }
                            this.props.vm.extensionManager.loadExtensionURL(proxyUrl)
                                .then(() => {
                                    this.props.onCategorySelected(extensionId);
                                })
                                .catch(() => {
                                    // eslint-disable-next-line no-alert
                                    alert(err);
                                });
                            return;
                        }
                        // eslint-disable-next-line no-alert
                        alert(err);
                    });
            }
        }
    }

    handleExtensionManagerClick () {
        const loadedExtensions = this.getLoadedExtensions();
        this.setState({
            extensionManagerOpen: true,
            loadedExtensions: loadedExtensions
        });
    }

    handleExtensionManagerClose () {
        this.setState({
            extensionManagerOpen: false
        });
    }

    // 懒创建用于「浏览积木」的独立 VM。预览扩展时只在这个 VM 上加载扩展、
    // 取积木用于展示，绝不把扩展注册进用户正在编辑的项目 VM（避免污染项目
    // 或触发扩展的副作用代码）。默认 securityManager 为 worker 沙盒，可静默加载。
    _getBrowseVM () {
        if (!this._browseVM) {
            this._browseVM = new VM();
            // 关键修复：把主编辑器 VM 上由 TWSecurityManager 注入的安全策略复制给浏览 VM。
            // 裸 new VM() 的默认策略是 'worker' 沙箱——在 Web Worker 里用跨域 importScripts
            // 拉扩展 JS，网络稍有波动就报 "NetworkError: Failed to execute 'importScripts'"。
            // 复制策略后，extensions.turbowarp.org / 自家扩展库等信任源与「添加扩展」一样
            // 走无沙箱 fetch 加载，行为完全一致。
            const mainSM = this.props.vm && this.props.vm.extensionManager &&
                this.props.vm.extensionManager.securityManager;
            const browseSM = this._browseVM.extensionManager.securityManager;
            if (mainSM && browseSM) {
                for (const method of Object.keys(mainSM)) {
                    if (typeof mainSM[method] === 'function') {
                        browseSM[method] = mainSM[method];
                    }
                }
            }
            // 信任源扩展直连失败、改经同源 /proxy 兜底重试时，代理 URL 同样按无沙箱加载。
            // （内容与直连完全一致，只是换了通道，不放大信任面。）
            this._browseTrustedProxyURLs = new Set();
            const baseGetSandboxMode = browseSM.getSandboxMode.bind(browseSM);
            browseSM.getSandboxMode = url => (
                this._browseTrustedProxyURLs.has(url) ? 'unsandboxed' : baseGetSandboxMode(url)
            );
        }
        return this._browseVM;
    }

    // 在扩展仓库中点击「浏览积木」：用独立的浏览 VM 加载扩展取积木预览，
    // 不把扩展注册进用户正在编辑的项目 VM。
    handleBrowseBlocks (extensionId) {
        // Special-case ids that are NOT real extension URLs (mirrors
        // handleExtensionClicked). Trying to loadExtensionURL on these throws
        // "Invalid extension URL: <id>", so handle them here first.
        if (extensionId === 'custom_extension') {
            this.props.onOpenCustomExtensionModal();
            return;
        }
        if (extensionId === 'procedures_enable_return') {
            this.props.onEnableProcedureReturns();
            this.props.onCategorySelected('myBlocks');
            return;
        }

        const browseVM = this._getBrowseVM();
        const extensionManager = browseVM.extensionManager;
        const runtime = browseVM.runtime;
        const openWithBlocks = (blocks, name) => {
            this.setState({
                browseExtensionId: extensionId,
                browseExtensionName: name || this._findExtensionName(extensionId),
                browseBlocks: blocks || []
            });
        };

        // Grab blocks for a given _blockInfo id (used for already-loaded extensions).
        const grabBlocksById = (id) => {
            if (runtime && runtime._blockInfo && Array.isArray(runtime._blockInfo)) {
                const info = runtime._blockInfo.find(i => i.id === id);
                return info ? (info.blocks || []) : [];
            }
            return [];
        };

        if (extensionManager.isExtensionLoaded(extensionId)) {
            openWithBlocks(grabBlocksById(extensionId));
            return;
        }

        // Try to find the extension's load URL.
        let url = null;
        const extensionURLs = extensionManager.getExtensionURLs ? extensionManager.getExtensionURLs() : {};
        if (extensionURLs && extensionURLs[extensionId]) {
            url = extensionURLs[extensionId];
        }
        if (!url) {
            const allLibraryExtensions = [
                ...(extensionLibraryContent || []),
                ...(this.state.gallery || []),
                ...(this.state.astraExtensions || []),
                ...(this.state.udbbsExtensions || []),
                ...(this.state.frostExtensions || [])
            ];
            const matched = allLibraryExtensions.find(ext => ext && ext.extensionId === extensionId);
            if (matched) {
                url = matched.extensionURL || extensionId;
            }
        }
        if (!url) {
            url = extensionId; // fall back to built-in extension id
        }

        // Snapshot ids BEFORE loading so we can find the extension that was just added
        // (its _blockInfo id may differ from the library extensionId for URL extensions).
        const beforeIds = new Set(
            (runtime && runtime._blockInfo) ? runtime._blockInfo.map(i => i.id) : []
        );

        const handleLoaded = () => {
            // Find the newly added extension entry.
            let loadedId = null;
            if (runtime && runtime._blockInfo) {
                const added = runtime._blockInfo.filter(i => !beforeIds.has(i.id));
                if (added.length) {
                    loadedId = added[added.length - 1].id;
                }
            }
            let blocks = loadedId ? grabBlocksById(loadedId) : [];
            if (blocks.length === 0) {
                // Fallback: lookup by the original library extensionId.
                blocks = grabBlocksById(extensionId);
            }
            if (blocks.length === 0) {
                // Last resort: match by block type prefix (id transformed on load).
                if (runtime && runtime._blockInfo) {
                    const info = runtime._blockInfo.find(i =>
                        i.blocks && i.blocks.some(b =>
                            b.info && b.info.opcode && b.info.opcode.startsWith(extensionId + '_')));
                    blocks = info ? (info.blocks || []) : [];
                }
            }
            openWithBlocks(blocks);
        };

        extensionManager.loadExtensionURL(url)
            .then(handleLoaded)
            .catch(err => {
                // 直连失败（常见于 extensions.turbowarp.org 网络不通导致的
                // importScripts / fetch NetworkError）→ 改经同源 /proxy 兜底重试。
                if (/^https?:\/\//i.test(url)) {
                    const proxyUrl = `/proxy?url=${encodeURIComponent(url)}`;
                    const securityManager = extensionManager.securityManager;
                    // 仅当原 URL 本身就是「无沙箱信任源」时，代理 URL 才继承无沙箱；
                    // 否则第三方扩展会借代理绕过 iframe 沙箱。
                    Promise.resolve(securityManager.getSandboxMode(url))
                        .then(mode => {
                            if (mode === 'unsandboxed' && this._browseTrustedProxyURLs) {
                                this._browseTrustedProxyURLs.add(proxyUrl);
                            }
                            return extensionManager.loadExtensionURL(proxyUrl);
                        })
                        .then(handleLoaded)
                        .catch(() => {
                            // eslint-disable-next-line no-alert
                            alert(err);
                        });
                    return;
                }
                // eslint-disable-next-line no-alert
                alert(err);
            });
    }

    _findExtensionName (extensionId) {
        // 优先从已加载信息里取
        if (this.state.extensionInfoMap && this.state.extensionInfoMap[extensionId]) {
            return this.state.extensionInfoMap[extensionId].name || extensionId;
        }
        // 再在各扩展库中匹配
        const allLibraryExtensions = [
            ...(extensionLibraryContent || []),
            ...(this.state.gallery || []),
            ...(this.state.astraExtensions || []),
            ...(this.state.udbbsExtensions || []),
            ...(this.state.frostExtensions || [])
        ];
        const matched = allLibraryExtensions.find(ext => ext && ext.extensionId === extensionId);
        if (matched) {
            return matched.name || extensionId;
        }
        return extensionId;
    }

    handleBrowseBlocksClose () {
        this.setState({
            browseExtensionId: null,
            browseExtensionName: '',
            browseBlocks: null
        });
    }

    getLoadedExtensions () {
        const extensionManager = this.props.vm.extensionManager;
        const runtime = this.props.vm.runtime;
        const loadedExtensions = [];

        // 核心扩展列表（这些是 Scratch 核心分类，不是扩展）
        const coreExtensions = [
            'motion', 'looks', 'sound', 'events', 'control', 'sensing',
            'operators', 'variables', 'myBlocks'
        ];

        // 从 _loadedExtensions 获取已加载的扩展
        if (extensionManager._loadedExtensions) {
            for (const [extensionId, serviceName] of extensionManager._loadedExtensions.entries()) {
                // 跳过核心扩展
                if (coreExtensions.includes(extensionId)) {
                    continue;
                }

                // 跳过自定义扩展入口
                if (extensionId === 'customExtension') {
                    continue;
                }

                // 从 runtime._blockInfo 中获取该扩展的积木信息
                let extBlockInfo = null;
                if (runtime && runtime._blockInfo && Array.isArray(runtime._blockInfo)) {
                    extBlockInfo = runtime._blockInfo.find(info => info.id === extensionId);
                }

                // 优先从事件保存的扩展信息中获取
                const savedInfo = this.state.extensionInfoMap[extensionId];
                let name = extensionId;
                let description = serviceName;
                let iconURL = null;
                let isBuiltin = false;

                // 检查是否是内置扩展
                if (extensionManager.isBuiltinExtension && extensionManager.isBuiltinExtension(extensionId)) {
                    isBuiltin = true;
                }

                if (savedInfo) {
                    name = savedInfo.name || extensionId;
                    iconURL = savedInfo.iconURL || null;
                    description = isBuiltin ? '内置扩展' : '';
                } else {
                    description = isBuiltin ? '内置扩展' : serviceName;
                }

                // 如果没有保存的信息，尝试从扩展库中匹配
                if (!savedInfo) {
                    // 获取已加载扩展的 URL
                    const extensionURLs = extensionManager.getExtensionURLs ? extensionManager.getExtensionURLs() : {};
                    const extURL = extensionURLs[extensionId];

                    // 收集所有扩展库中的扩展，用于匹配
                    const allLibraryExtensions = [];

                    // 添加本地扩展
                    if (extensionLibraryContent && Array.isArray(extensionLibraryContent)) {
                        allLibraryExtensions.push(...extensionLibraryContent);
                    }

                    // 添加 TurboWarp 扩展
                    if (this.state.gallery && Array.isArray(this.state.gallery)) {
                        allLibraryExtensions.push(...this.state.gallery);
                    }

                    // 添加 AstraEditor 扩展
                    if (this.state.astraExtensions && Array.isArray(this.state.astraExtensions)) {
                        allLibraryExtensions.push(...this.state.astraExtensions);
                    }

                    // 添加 UDBBS 扩展
                    if (this.state.udbbsExtensions && Array.isArray(this.state.udbbsExtensions)) {
                        allLibraryExtensions.push(...this.state.udbbsExtensions);
                    }

                    // 添加 FrostEditor 扩展
                    if (this.state.frostExtensions && Array.isArray(this.state.frostExtensions)) {
                        allLibraryExtensions.push(...this.state.frostExtensions);
                    }

                    // 规范化 URL，用于匹配
                    const normalizeURL = (url) => {
                        if (!url) return '';
                        try {
                            const u = new URL(url);
                            return `https://${u.host}${u.pathname}`.toLowerCase();
                        } catch (e) {
                            return url.toLowerCase();
                        }
                    };

                    // 尝试通过 URL 匹配
                    let matchedExtension = null;
                    if (extURL) {
                        const normalizedURL = normalizeURL(extURL);
                        matchedExtension = allLibraryExtensions.find(ext =>
                            ext.extensionURL && normalizeURL(ext.extensionURL) === normalizedURL
                        );
                    }

                    // 如果没找到，尝试通过 extensionId 匹配
                    if (!matchedExtension) {
                        matchedExtension = allLibraryExtensions.find(ext =>
                            ext.extensionId === extensionId
                        );
                    }

                    if (matchedExtension) {
                        name = matchedExtension.name || extensionId;
                        description = isBuiltin ? '内置扩展' : (matchedExtension.description || '');
                        iconURL = matchedExtension.iconURL || null;
                    }
                }

                loadedExtensions.push({
                    id: extensionId,
                    name: name,
                    description: description,
                    iconURL: iconURL,
                    isBuiltin: isBuiltin,
                    blocks: extBlockInfo ? extBlockInfo.blocks || [] : []
                });
            }
        }

        return loadedExtensions;
    }

    handleRemoveExtension (extensionId) {
        const extensionManager = this.props.vm.extensionManager;
        const runtime = this.props.vm.runtime;

        try {
            // 0. 获取该扩展的所有积木 opcode
            //    注意：runtime._blockInfo 里每个 block 的结构是
            //    { info: { opcode: <裸 opcode> }, json: { type: '<extensionId>_<opcode>' }, xml }
            //    因此积木在运行期/工作区里的实际 type/opcode 都是 '<extensionId>_<opcode>'
            //    （见 scratch-vm runtime._convertBlockForScratchBlocks）。
            //    这里同时收集裸 opcode 与扩展后 opcode，确保工作区与运行期的积木都能被正确匹配删除。
            const extensionOpcodes = new Set();
            if (runtime._blockInfo && Array.isArray(runtime._blockInfo)) {
                const extInfo = runtime._blockInfo.find(info => info.id === extensionId);
                if (extInfo && extInfo.blocks) {
                    for (const block of extInfo.blocks) {
                        const bare = (block.info && block.info.opcode) || block.opcode;
                        if (bare) {
                            extensionOpcodes.add(bare);
                            extensionOpcodes.add(`${extensionId}_${bare}`);
                        }
                        const jsonType = block.json && block.json.type;
                        if (jsonType) {
                            extensionOpcodes.add(jsonType);
                        }
                    }
                }
            }

            // 0.1 从 VM 中删除积木
            if (runtime && runtime.targets && extensionOpcodes.size > 0) {
                const blocksToDelete = [];

                for (const target of runtime.targets) {
                    if (target.blocks && target.blocks._blocks) {
                        const blocks = target.blocks._blocks;
                        for (const blockId in blocks) {
                            if (Object.prototype.hasOwnProperty.call(blocks, blockId)) {
                                const block = blocks[blockId];
                                if (block.opcode && extensionOpcodes.has(block.opcode)) {
                                    blocksToDelete.push({target, blockId});
                                }
                            }
                        }
                    }
                }

                for (const {target, blockId} of blocksToDelete) {
                    if (target.blocks && target.blocks.deleteBlock) {
                        target.blocks.deleteBlock(blockId);
                    }
                }
            }

            // 0.2 直接从 Blockly 工作区删除积木（确保 UI 上也消失）
            const Blockly = window.ScratchBlocks || window.Blockly;
            if (Blockly) {
                const workspace = Blockly.getMainWorkspace();
                if (workspace) {
                    const allBlocks = workspace.getAllBlocks();
                    const blocksToRemove = allBlocks.filter(block => {
                        return extensionOpcodes.has(block.type);
                    });
                    for (const block of blocksToRemove) {
                        block.dispose(true);
                    }
                }
            }

            // 0.3 重新序列化工作区，使项目中不再包含已删除的积木
            if (this.props.vm.emitWorkspaceUpdate) {
                this.props.vm.emitWorkspaceUpdate();
            }

            // 1. 检查扩展是否存在并获取 serviceName
            let serviceName = null;
            if (extensionManager._loadedExtensions instanceof Map) {
                serviceName = extensionManager._loadedExtensions.get(extensionId);
            } else if (typeof extensionManager._loadedExtensions === 'object') {
                serviceName = extensionManager._loadedExtensions[extensionId];
            }

            if (!serviceName) {
                alert(`扩展 ${extensionId} 未找到`);
                return;
            }

            // 2. 从 runtime 的 _blockInfo 中移除扩展分类
            if (runtime._blockInfo && Array.isArray(runtime._blockInfo)) {
                const blockInfoIndex = runtime._blockInfo.findIndex(info => info.id === extensionId);
                if (blockInfoIndex !== -1) {
                    runtime._blockInfo.splice(blockInfoIndex, 1);
                }
            }

            // 3. 清理 worker 相关信息（如果是 worker 模式）
            if (typeof serviceName === 'string') {
                const workerIdMatch = serviceName.match(/extension_(\d+)_/);
                if (workerIdMatch) {
                    const workerId = parseInt(workerIdMatch[1]);
                    if (extensionManager.workerURLs && extensionManager.workerURLs[workerId]) {
                        delete extensionManager.workerURLs[workerId];
                    }
                    if (extensionManager.pendingWorkers && extensionManager.pendingWorkers[workerId]) {
                        delete extensionManager.pendingWorkers[workerId];
                    }
                }
            }

            // 4. 从 _loadedExtensions 中移除
            if (extensionManager._loadedExtensions instanceof Map) {
                extensionManager._loadedExtensions.delete(extensionId);
            } else if (typeof extensionManager._loadedExtensions === 'object') {
                delete extensionManager._loadedExtensions[extensionId];
            }

            // 5. 触发扩展移除事件，通知 UI 更新
            if (this.props.vm.emit) {
                this.props.vm.emit('EXTENSION_REMOVED', {id: extensionId});
            }

            // 6. 同步到协作房间（如果在协作中）
            try {
                if (collaborationManager && collaborationManager.isConnected) {
                    collaborationManager.sendExtensionUnload(extensionId);
                }
            } catch (e) {
                // 忽略协作同步错误
            }

            // 7. 刷新扩展列表
            const loadedExtensions = this.getLoadedExtensions();
            this.setState({
                loadedExtensions: loadedExtensions
            });

            // 7. 提示用户
            alert(`扩展 ${extensionId} 已卸载。`);
        } catch (error) {
            console.error('Failed to remove extension:', error);
            alert(`卸载扩展失败：${error.message}`);
        }
    }

    render () {
        // Always render the built-in extensions so their cards (and the
        // "Browse blocks" button) are visible even before the online gallery
        // finishes loading or fails.
        let library = extensionLibraryContent.map(toLibraryItem);
        const remoteReady = this.state.gallery || this.state.galleryError || this.state.galleryTimedOut ||
            this.state.astraExtensions || this.state.astraError ||
            this.state.udbbsExtensions || this.state.udbbsError ||
            this.state.frostExtensions || this.state.frostError;
        if (remoteReady) {
            library.push('---');
            if (this.state.gallery) {
                library.push(toLibraryItem(galleryMore));
                const locale = this.props.intl.locale;
                library.push(
                    ...this.state.gallery
                        .filter(i => i.extensionId !== 'faceSensing')
                        .map(i => translateGalleryItem(i, locale))
                        .map(toLibraryItem)
                );
            } else if (this.state.galleryError) {
                library.push(toLibraryItem(galleryError));
            } else {
                library.push(toLibraryItem(galleryLoading));
            }

            // 添加 FrostEditor 官方扩展仓库（我们自己的扩展仓库，排在第三方库之前）
            if (this.state.frostExtensions && this.state.frostExtensions.length > 0) {
                library.push('---');
                library.push(toLibraryItem({
                    name: 'FrostEditor 扩展库',
                    extensionId: 'frosteditor-gallery',
                    iconURL: `${FROST_BASE}frosteditor.png`,
                    description: `来自 FrostEditor 官方扩展仓库的扩展收集，前往 ${FROST_BASE} 查看更多。`,
                    href: FROST_BASE,
                    tags: ['frosteditor'],
                    featured: true
                }));
                const locale = this.props.intl.locale;
                library.push(
                    ...this.state.frostExtensions
                        .map(i => translateGalleryItem(i, locale))
                        .map(toLibraryItem)
                );
            }

            // 添加 AstraEditor 扩展
            if (this.state.astraExtensions && this.state.astraExtensions.length > 0) {
                library.push('---');
                library.push(toLibraryItem({
                    name: 'AstraEditor 扩展库',
                    extensionId: 'astra-editor-gallery',
                    iconURL: 'https://editors.astras.top/favicon.ico',
                    description: '来自 AstraEditor 的扩展收集',
                    href: 'https://editors.astras.top/extensions',
                    tags: ['astra-editor'],
                    featured: true
                }));
                const locale = this.props.intl.locale;
                library.push(
                    ...this.state.astraExtensions
                        .map(i => translateGalleryItem(i, locale))
                        .map(toLibraryItem)
                );
            }

            // 添加 UDBBS 扩展
            if (this.state.udbbsExtensions && this.state.udbbsExtensions.length > 0) {
                library.push('---');
                library.push(toLibraryItem({
                    name: 'UDBBS 扩展库',
                    extensionId: 'udbbs-gallery',
                    iconURL: 'https://extensions.udbbs.top/favicon.ico',
                    description: '来自 UDBBS 的扩展收集',
                    href: 'https://extensions.udbbs.top/',
                    tags: ['udbbs'],
                    featured: true
                }));
                const locale = this.props.intl.locale;
                library.push(
                    ...this.state.udbbsExtensions
                        .map(i => translateGalleryItem(i, locale))
                        .map(toLibraryItem)
                );
            }


            // 去重：屏蔽与其他分类 ID 相同的扩展
            const seenExtensionIds = new Set();
            const deduplicatedLibrary = [];

            for (const item of library) {
                // 分隔符直接添加
                if (item === '---') {
                    deduplicatedLibrary.push(item);
                    continue;
                }

                // 如果是对象且有 extensionId，检查是否重复
                if (typeof item === 'object' && item.extensionId) {
                    // 跳过特殊的分类标题项（如 gallery 标题）
                    if (item.href && item.extensionId.endsWith('-gallery')) {
                        deduplicatedLibrary.push(item);
                        continue;
                    }

                    // 如果已经出现过相同的 extensionId，跳过
                    if (seenExtensionIds.has(item.extensionId)) {
                        continue;
                    }
                    seenExtensionIds.add(item.extensionId);
                }

                deduplicatedLibrary.push(item);
            }

            library = deduplicatedLibrary;
        }

        return (
            <React.Fragment>
                <LibraryComponent
                    data={library}
                    filterable
                    persistableKey="extensionId"
                    id="extensionLibrary"
                    tags={extensionTags}
                    title={this.props.intl.formatMessage(messages.extensionTitle)}
                    visible={this.props.visible}
                    onItemSelected={this.handleItemSelect}
                    onRequestClose={this.props.onRequestClose}
                    onExtensionManagerClick={this.handleExtensionManagerClick}
                    onBrowseBlocks={this.handleBrowseBlocks}
                />
                {this.state.extensionManagerOpen && (
                    <ExtensionManagerModal
                        extensions={this.state.loadedExtensions}
                        onRemoveExtension={this.handleRemoveExtension}
                        onClose={this.handleExtensionManagerClose}
                        vm={this.props.vm}
                    />
                )}
                {this.state.browseExtensionId && (
                    <ExtensionBlocksModal
                        vm={this._browseVM || this.props.vm}
                        extensionId={this.state.browseExtensionId}
                        blocks={this.state.browseBlocks}
                        extensionName={this.state.browseExtensionName}
                        onClose={this.handleBrowseBlocksClose}
                    />
                )}
            </React.Fragment>
        );
    }
}

// Render a floating manage button inside the extension library modal area
ExtensionLibrary.prototype.render = ExtensionLibrary.prototype.render || ExtensionLibrary.prototype.render;

// Append FAB after component mount using portal-like approach in this file's render flow
// Simpler: patching to render the button alongside the LibraryComponent via wrapping div

ExtensionLibrary.propTypes = {
    intl: intlShape.isRequired,
    onCategorySelected: PropTypes.func,
    onEnableProcedureReturns: PropTypes.func,
    onOpenCustomExtensionModal: PropTypes.func,
    onRequestClose: PropTypes.func,
    visible: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired // eslint-disable-line react/no-unused-prop-types
};

export default injectIntl(ExtensionLibrary);
